import './styles/productPage.scss'

import { isCustomProduct, isGiftCard, state } from './store/state'
import data from './store/data'
import triggers from './store/triggers'
import elements from './store/elements'
import archive from './store/archive'

import { debounce } from '../../js/debounce'
import price from './components/price'
import addButton from './components/add.button'
import addAjax from './components/add.ajax'
import history from './history/history'
import { pageProductSEO } from './page.product.SEO'
import { isNullOrUndef } from '../../js/helpers/isNullOrUndef'
import { isNotNullOrUndef } from '../../js/helpers/isNotNullOrUndef'
import { dlProductAllPhotosUploadedEvent } from '../../events/product/dl.product.allPhotosUploaded.event'
import { dlProductOtherProductViewedEvent } from '../../events/product/dl.product.otherProductViewed.event'
import { dlProductPhotoUploadedEvent } from '../../events/product/dl.product.photoUploaded.event'
import { frameColor } from './components/frame color/frameColor'

const pp = window.productPage

// Get current Variant
// =============================================================================
const getCurrentVariant = debounce(() => {
  const currentOptions = []
  elements.layouts.length > 0 && currentOptions.push(
    state.type && !state.layout.includes(`${state.type} - `)
      ? `${state.type} - ${state.layout}`
      : state.layout
  )
  elements.sizes.length > 0 && currentOptions.push(state.size)
  elements.depths.length > 0 && currentOptions.push(state.depth)
  elements.amounts.length > 0 && currentOptions.push(state.amount)
  elements.colors.length > 0 && currentOptions.push(state.color)

  state.variant = data.variants.find(item => {
    return currentOptions.every(value => item.options.includes(value))
  })
  triggers.emitEvent('state:variant', [state.variant])
  if (!isCustomProduct) {
    state.cartDisabled = !state.variant
    triggers.emitEvent('state:cartDisabled', [state.cartDisabled])
  }
}, 150)
// triggers.on(`state:${data.options[data.options.length - 1]}`, getCurrentVariant) // Subscribe for changes of last option
triggers.on(['state:type', 'state:layout', 'state:size', 'state:depth', 'state:amount', 'state:color'], getCurrentVariant)

// Exports to window productPage
// =============================================================================
pp.getData = () => {
  return {
    type: state.type,
    layout: state.layout,
    size: state.size,
    depth: state.depth,
    amount: state.amount,
    color: state.color,
    frameColor: state.frameColor,
    product: {
      title: elements.title.textContent
    },
    variant: state.variant,
    archive
  }
}
pp.setData = (key, value) => {
  state[key] = value
  triggers.emitEvent(`state:${key}`, [value])
}
pp.watcher = {
  type (callback) { triggers.on('state:type', callback) },
  layout (callback) { triggers.on('state:layout', callback) },
  size (callback) { triggers.on('state:size', callback) },
  depth (callback) { triggers.on('state:depth', callback) },
  amount (callback) { triggers.on('state:amount', callback) },
  color (callback) { triggers.on('state:color', callback) },
  frameColor (callback) { triggers.on('state:frameColor', callback) },
  variant (callback) { triggers.on('state:variant', callback) }
}

// Subscribe to event 'change'
// =============================================================================
elements.form.addEventListener('change', ({ target }) => {
  const newValue = target.dataset.avalue
  switch (target.dataset.optionName) {
    case 'Type':
      if (state.type === newValue) return
      state.type = newValue
      triggers.emitEvent('state:type', [newValue])
      break
    case 'Layout':
      if (state.layout === newValue) return
      state.layout = newValue
      triggers.emitEvent('state:layout', [newValue])
      break
    case 'Size':
      if (state.size === newValue && (isGiftCard || isCustomProduct)) return
      state.size = newValue
      triggers.emitEvent('state:size', [newValue])
      break
    case 'Depth':
      if (state.depth === newValue) return
      state.depth = newValue
      triggers.emitEvent('state:depth', [newValue])
      break
    case 'Amount':
      if (state.amount === newValue) return
      state.amount = newValue
      triggers.emitEvent('state:amount', [newValue])
      state.imgIndex = elements.amounts.indexOf(target.parentElement.querySelector('label'))
      triggers.emitEvent('state:imgIndex', [state.imgIndex])
      break
    case 'Color':
      if (state.color === newValue) return
      state.color = newValue
      triggers.emitEvent('state:color', [newValue])
      break
    case 'Frame Color':
      if (state.frameColor === newValue) return
      state.frameColor = newValue
      triggers.emitEvent('state:frameColor', [newValue])
      break
  }
})

// Dispatch event
document.dispatchEvent(new window.CustomEvent('jsProductPageLoaded'))

// Components
// =============================================================================
;(async function () {
  // Main part of custom products
  if (isCustomProduct) {
    await import('uploadcare-widget').then(({ default: uploadcare }) => { window.uploadcare = uploadcare })
    await import('uploadcare-widget-tab-effects').then(({ default: uploadcareTabEffects }) => { window.uploadcareTabEffects = uploadcareTabEffects })

    if (['sets', 'collage', 'hexagon'].includes(window.custom_template)) {
      await import('./custom/collage/page.product.custom-collage')
    } else if (window.custom_template === 'foam') {
      await import('./custom/foam/page.product.custom-foam')
    } else {
      await import('./custom/basic/page.product.custom-basic')
    }

    await import('./custom/page.product.custom')
  }
  // Get/Set params from URL
  history()
  // price
  price()
  // add to cart button
  addButton()
  // custom products part
  if (isCustomProduct) {
    if (['basic', 'frames'].includes(window.custom_template)) {
      import('./components/customProduct.basic').then(({ default: customProductBasic }) => { customProductBasic() })
    } else if (['hexagon', 'sets', 'collage'].includes(window.custom_template)) {
      import('./components/customProduct.sets').then(({ default: customProductSets }) => { customProductSets() })
    }
  }
  // options
  elements.types.length > 0 && import('./components/types').then(({ default: types }) => { types() })
  elements.layouts.length > 0 && import('./components/layouts').then(({ default: layouts }) => { layouts() })
  elements.sizes.length > 0 && import('./components/sizes').then(({ default: sizes }) => { sizes() })
  elements.depths.length > 0 && import('./components/depths').then(({ default: depths }) => { depths() })
  elements.colors.length > 0 && import('./components/colors').then(({ default: colors }) => { colors() })
  elements.amounts.length > 0 && import('./components/amounts').then(({ default: amounts }) => { amounts() })
  // images
  if (!isCustomProduct) {
    import('./components/images.standard').then(({ default: images }) => { images() })
  } else if (window.custom_template !== 'foam') {
    import('./components/images.custom').then(({ default: images }) => { images() })
  }
  // ajax logic
  addAjax()
  // add to cart modal
  import('./components/add.modal').then(({ default: modal }) => { modal() })
  // images in reviews
  import('./components/reviews.photo').then(({ default: photos }) => { photos() })
  // gift card terms tooltip
  isGiftCard && import('./components/giftCard.terms').then(({ default: terms }) => { terms() })
  // SEO
  pageProductSEO(state.variant)
  triggers.on('state:variant', pageProductSEO)

  // Frame color
  if (!isGiftCard) {
    triggers.on(['state:layout', 'state:frameColor'], frameColor)
  }
}())

// Another logic
// =============================================================================
// Custom Add more photos
if (isCustomProduct && window.custom_template !== 'foam') {
  document.querySelector('.add_more_photos_btn').addEventListener('click', () => {
    document.querySelector('#image_uploader_more_photos').nextElementSibling.querySelector('.uploadcare--widget__button_type_open').dispatchEvent(new window.MouseEvent('click'))
  })
}

// Reviews
document.querySelectorAll('.product-reviews-link').forEach(elem => elem.addEventListener('click', event => {
  event.preventDefault()

  const newTabsWrapper = document.querySelector('.product__reviews_section')
  if (newTabsWrapper) {
    window.scrollTo({ top: newTabsWrapper.getBoundingClientRect().top + window.pageYOffset, behavior: 'smooth' })
  }
}))

// Social Share
if (window.innerWidth < 1200) {
  const wrapper = document.querySelector('.product__social-sharing--mob')

  if (isNotNullOrUndef(wrapper)) {
    wrapper.querySelector('button').addEventListener('click', () => {
      wrapper.classList.toggle('active')
    })
  }
}

// Shake size chart button
;(function () {
  let count = 0
  for (const elem of document.querySelectorAll('.regular-product-Size-ow label')) {
    const handler = () => {
      if (++count === 3) {
        document.querySelector('.size-question-wrapper--sc').classList.add('shake-it')
        elem.removeEventListener('click', handler)
      }
    }
    elem.addEventListener('click', handler)
  }
}())

// Discount banner
;(function () {
  const condition = document.querySelector('.product__discount-banner_condition')
  const congrats = document.querySelector('.product__discount-banner_congrats')
  const banner = document.querySelector('.product__discount-banner')

  if (!condition || !congrats || !banner) return

  const minAmount = banner.dataset.minAmount
  let isLoading = true

  if (!minAmount) return

  let attempt = 0
  const subscribe = setInterval(() => {
    ++attempt > 9 && clearInterval(subscribe)
    if (!window.cart || !window.cart.on) return

    clearInterval(subscribe)

    window.cart.on('data', ({ total }) => {
      if (isLoading) {
        isLoading = false
        return
      }
      const isMore = total >= +minAmount
      condition.hidden = isMore
      congrats.hidden = !isMore
    })
  }, 1000)
}())

// EVENTS (GTM)
// =============================================================================
;(function () {
  // User see product / variant
  dlProductOtherProductViewedEvent()
  const debouncedEvent = debounce(dlProductOtherProductViewedEvent, 300)

  for (const option of data.options) {
    triggers.on(`state:${option}`, debouncedEvent)
  }

  // Custom products upload photo
  if (isCustomProduct) {
    document.addEventListener('imageLoaded', event => {
      const fileInfo = event.detail
      const productInfo = window.productPage.getData()

      dlProductPhotoUploadedEvent(productInfo.type, productInfo.layout, fileInfo)
    })

    document.addEventListener('allImagesUploaded', () => {
      const productInfo = window.productPage.getData()

      dlProductAllPhotosUploadedEvent(productInfo.type, productInfo.layout)
    })
  }
}())

;(() => {
  const oqInterval = setInterval(() => {
    if (window.matchMedia('(min-width: 768px) and (max-width: 1200px)').matches) {
      const oqSection = document.querySelector('.shopify-section.our-quality__section')
      const pdSection = document.querySelector('.shopify-section.product__description_section')
      // console.log('interval')
      if (isNullOrUndef(oqSection) || isNullOrUndef(pdSection)) return

      clearInterval(oqInterval)
      pdSection.append(oqSection)
    }
  }, 100)
})()
